import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Calculator, FileText, Ship, TrendingUp, Landmark, Users,
  BookOpenCheck, Database, Bell, Moon, Sun, Search, ChevronLeft, ChevronRight, X, AlertTriangle,
  Link2, ReceiptText, Waves,
} from "lucide-react";
import { useApp, ROLES } from "../context/AppContext";
import api from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Input } from "./ui/input";

const NAV = [
  { id: "dashboard", to: "/", label: "Dashboard", Icon: LayoutDashboard },
  { id: "deals", to: "/deals", label: "Deal Calculation", Icon: Calculator },
  { id: "contracts", to: "/contracts", label: "Contracts", Icon: FileText },
  { id: "linking", to: "/linking", label: "Back-to-Back", Icon: Link2 },
  { id: "vessels", to: "/vessels", label: "Vessels & Logistics", Icon: Ship },
  { id: "positions", to: "/positions", label: "Position & Risk", Icon: TrendingUp },
  { id: "finance", to: "/finance", label: "Trade Finance", Icon: Landmark },
  { id: "invoices", to: "/invoices", label: "Invoices", Icon: ReceiptText },
  { id: "fundflow", to: "/fund-flow", label: "Fund Flow", Icon: Waves },
  { id: "counterparties", to: "/counterparties", label: "Counterparties", Icon: Users },
  { id: "accounting", to: "/accounting", label: "Accounting / GL", Icon: BookOpenCheck },
  { id: "masters", to: "/masters", label: "Masters", Icon: Database },
];

const Logo = ({ small }) => (
  <div className="flex items-center gap-2">
    <div className="h-9 w-9 rounded-md bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center shadow-sm">
      <span className="text-white font-extrabold text-sm tracking-tight">RA</span>
    </div>
    {!small && (
      <div className="leading-tight">
        <p className="font-extrabold text-slate-900 dark:text-slate-100 text-sm">Riya Agro</p>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">Singapore ERP</p>
      </div>
    )}
  </div>
);

function NotificationsPanel() {
  const { data = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications"), refetchInterval: 15000 });
  const sev = { critical: "text-rose-600 bg-rose-50 dark:bg-rose-950/40", warning: "text-amber-600 bg-amber-50 dark:bg-amber-950/40", info: "text-sky-600 bg-sky-50 dark:bg-sky-950/40" };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
          <Bell className="h-5 w-5" />
          {data.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center font-bold">{data.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 font-bold text-sm">Alerts & Notifications</div>
        <div className="max-h-96 overflow-y-auto erp-scroll">
          {data.length === 0 && <p className="p-4 text-sm text-slate-400">No active alerts</p>}
          {data.map((a, i) => (
            <div key={i} data-testid={`notification-${i}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800/60">
              <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${sev[a.severity]}`}><AlertTriangle className="h-4 w-4" /></div>
              <p className="text-sm text-slate-700 dark:text-slate-300">{a.message}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: pcs = [] } = useQuery({ queryKey: ["gs-pc"], queryFn: () => api.get("/purchase-contracts") });
  const { data: scs = [] } = useQuery({ queryKey: ["gs-sc"], queryFn: () => api.get("/sale-contracts") });
  const { data: vessels = [] } = useQuery({ queryKey: ["gs-v"], queryFn: () => api.get("/vessels") });
  const { data: cps = [] } = useQuery({ queryKey: ["gs-cp"], queryFn: () => api.get("/counterparties") });
  const s = q.toLowerCase();
  const results = !q ? [] : [
    ...pcs.filter((c) => (c.contract_ref || "").toLowerCase().includes(s)).map((c) => ({ label: c.contract_ref, sub: `Purchase · ${c.commodity}`, to: "/contracts" })),
    ...scs.filter((c) => (c.contract_ref || "").toLowerCase().includes(s)).map((c) => ({ label: c.contract_ref, sub: `Sale · ${c.commodity}`, to: "/contracts" })),
    ...vessels.filter((v) => (v.name || "").toLowerCase().includes(s)).map((v) => ({ label: v.name, sub: "Vessel", to: "/vessels" })),
    ...cps.filter((c) => (c.name || "").toLowerCase().includes(s)).map((c) => ({ label: c.name, sub: `Counterparty · ${c.type}`, to: "/counterparties" })),
  ].slice(0, 8);
  return (
    <div className="relative hidden md:block">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input data-testid="global-search" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search contracts, vessels, counterparties…" className="pl-8 h-9 w-80 bg-slate-100 dark:bg-slate-800 border-transparent" />
      {open && results.length > 0 && (
        <div className="absolute mt-1 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg z-50 overflow-hidden">
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => { navigate(r.to); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-slate-800 flex justify-between items-center">
              <span className="text-sm font-medium">{r.label}</span>
              <span className="text-xs text-slate-400">{r.sub}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children, title }) {
  const { role, setRole, theme, setTheme, entity, setEntity, can } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const { data: entities = [] } = useQuery({ queryKey: ["entities"], queryFn: () => api.get("/entities") });
  const visibleNav = NAV.filter((n) => can(n.id));

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-60"} shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col sticky top-0 h-screen transition-all`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800">
          <Logo small={collapsed} />
          <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto erp-scroll">
          {visibleNav.map(({ id, to, label, Icon }) => (
            <NavLink key={id} to={to} end={to === "/"} data-testid={`nav-${id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-orange-50 text-orange-600 font-semibold dark:bg-orange-950/40 dark:text-orange-400"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/60"
                }`}>
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>
        {!collapsed && (
          <div className="p-3 border-t border-slate-200 dark:border-slate-800">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 px-1">Active Desk</p>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="role-switcher" className="h-9 bg-slate-50 dark:bg-slate-800 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`role-${r.id}`}>{r.name} · {r.desk}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 gap-4">
          <h1 className="text-lg font-extrabold text-slate-900 dark:text-slate-100 tracking-tight truncate">{title}</h1>
          <div className="flex items-center gap-3">
            <GlobalSearch />
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="entity-selector" className="h-9 w-44 bg-slate-100 dark:bg-slate-800 text-xs hidden lg:flex">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.code}</SelectItem>)}
              </SelectContent>
            </Select>
            <NotificationsPanel />
            <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <div className="flex items-center gap-2 pl-2 border-l border-slate-200 dark:border-slate-800">
              <div className="h-8 w-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-xs font-bold">
                {ROLES.find((r) => r.id === role)?.name[0]}
              </div>
              <div className="hidden xl:block leading-tight">
                <p className="text-xs font-semibold">{ROLES.find((r) => r.id === role)?.name}</p>
                <p className="text-[10px] text-slate-400">{ROLES.find((r) => r.id === role)?.desk}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
